import React, { useMemo, useState } from "react";
// Adjust these imports to your actual shadcn paths:
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Loader2 } from "lucide-react";
import { toast } from "react-toastify";

const testingBase = process.env.REACT_APP_TESTING_BASE;
const prodBase = process.env.REACT_APP_API_BASE;
const grabInvoices = process.env.REACT_APP_GRAB_INVOICES;

// Parse names like: V22966_SCB_2023147_11.01.2023[_FA].pdf
function parseInvoiceName(name) {
    // Matches: V<digits>_<vendor>_<invoice>_<MM.DD.YYYY>[_FA].pdf
    // invoice may be alphanumeric and include . or - (e.g., CRFT12345, 5737.50, INV-205810)
    const base = name.replace(/\.pdf$/i, "");
    const re = /^V(\d+)_([A-Za-z0-9-]+)_([A-Za-z0-9.-]+)_(\d{2}\.\d{2}\.\d{4})(?:_FA)?$/i;

    const m = base.match(re);
    if (!m) return null;

    const [, voucher, vendor, invoice, date] = m;
    const fa = /_FA$/i.test(base);

    return { voucher, vendor, invoice, date, fa };
}



export default function VoucherSearch({ onResults, onFileSelect }) {
    const [voucher, setVoucher] = useState("");
    const [vendor, setVendor] = useState("");
    const [invoice, setInvoice] = useState("");
    const [date, setDate] = useState(""); // expects 11.01.2023
    const [faOnly, setFaOnly] = useState(false);
    const [freeText, setFreeText] = useState("");
    const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
    const [invoiceFiles, setInvoiceFiles] = useState([]);
    const [selectedFileIndex, setSelectedFileIndex] = useState(null)
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [selectedIds, setSelectedIds] = useState(new Set());
    const [downloading, setDownloading] = useState(false);

    const chosenQuery = useMemo(() => {
        if (invoice.trim()) return invoice.trim();
        if (voucher.trim()) {
            const normalized = voucher.trim().toUpperCase();
            return /^\d+$/.test(normalized) ? `V${normalized}` : normalized;
        }
        if (vendor.trim()) return vendor.trim();

        // When filtering by date range, we need all PDFs
        // because Graph search can't handle date ranges
        if (startDate.trim() || endDate.trim()) {
            return ".pdf";
        }

        return ".pdf";
    }, [invoice, voucher, vendor, startDate, endDate]);


    function normalizeDateQuery(q) {
        if (!q) return "";
        // Accept 11/2023, 11-2023, 11.2023, etc.
        let s = q.trim().replace(/[/-]/g, ".").replace(/\s+/g, "");
        // If user enters M.YYYY (e.g., 1.2023), pad month
        const mMonthYear = /^(\d{1})\.(\d{4})$/.exec(s);
        if (mMonthYear) s = `${mMonthYear[1].padStart(2, "0")}.${mMonthYear[2]}`;
        return s;
    }

    function splitFileDate(mmddyyyy) {
        const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(mmddyyyy);
        if (!m) return null;
        return { mm: m[1], dd: m[2], yyyy: m[3] };
    }

    function dateToComparable(dateStr) {
        const parts = splitFileDate(dateStr);
        if (!parts) return null;
        return parseInt(`${parts.yyyy}${parts.mm}${parts.dd}`);
    }

    function partialDateToRange(query) {
        const normalized = normalizeDateQuery(query);

        // YYYY only
        if (/^\d{4}$/.test(normalized)) {
            return {
                start: parseInt(`${normalized}0101`),
                end: parseInt(`${normalized}1231`)
            };
        }

        // MM.YYYY
        if (/^\d{2}\.\d{4}$/.test(normalized)) {
            const [mm, yyyy] = normalized.split(".");
            const lastDay = new Date(parseInt(yyyy), parseInt(mm), 0).getDate();
            return {
                start: parseInt(`${yyyy}${mm}01`),
                end: parseInt(`${yyyy}${mm}${lastDay}`)
            };
        }

        // MM.DD.YYYY (full date)
        if (/^\d{2}\.\d{2}\.\d{4}$/.test(normalized)) {
            const [mm, dd, yyyy] = normalized.split(".");
            const comparable = parseInt(`${yyyy}${mm}${dd}`);
            return {
                start: comparable,
                end: comparable
            };
        }

        return null;
    }

    // fileDate is the filename token like "11.01.2023"
    // q can be "2023", "11.2023", or "11.01.2023"
    function matchesDateRange(fileDate, startQuery, endQuery) {
        const fileComparable = dateToComparable(fileDate);
        if (!fileComparable) return false;

        // If both start and end dates are provided
        if (startQuery && endQuery) {
            const startRange = partialDateToRange(startQuery);
            const endRange = partialDateToRange(endQuery);

            if (!startRange || !endRange) return false;

            return fileComparable >= startRange.start && fileComparable <= endRange.end;
        }

        // If only start date is provided
        if (startQuery) {
            const range = partialDateToRange(startQuery);
            if (!range) return false;
            return fileComparable >= range.start && fileComparable <= range.end;
        }

        // If only end date is provided
        if (endQuery) {
            const range = partialDateToRange(endQuery);
            if (!range) return false;
            return fileComparable >= range.start && fileComparable <= range.end;
        }

        return false;
    }

    async function handleSearch() {
        setLoading(true);
        setError(null);
        try {
            const invoiceFiles = await handleFetchInvoiceFiles();

            // If using free text search and no other filters, show all backend results
            const hasSpecificFilters = voucher || vendor || invoice || date || faOnly;

            // Client-side strict filtering against parsed pieces
            const filtered = invoiceFiles.filter((f) => {
                const p = parseInvoiceName(f.name);
                if (!p) {
                    return !hasSpecificFilters;
                }

                // Normalize voucher for comparison (remove "V" prefix)
                if (voucher) {
                    const normalizedVoucher = voucher.trim().toUpperCase().replace(/^V/, '');
                    if (p.voucher !== normalizedVoucher) return false;
                }

                if (vendor && p.vendor.toUpperCase() !== vendor.trim().toUpperCase()) return false;
                if (invoice && p.invoice !== invoice.trim()) return false;
                // Check date range
                if ((startDate || endDate) && !matchesDateRange(p.date, startDate, endDate)) return false;
                if (faOnly && !p.fa) return false;

                return true;
            });

            setInvoiceFiles(filtered);
            if (typeof onResults === "function") onResults(filtered);
        } catch (e) {
            setError(e?.message ?? String(e));
            setInvoiceFiles([]);
        } finally {
            setLoading(false);
        }
    }

    const handleFetchInvoiceFiles = async () => {
        setIsLoadingInvoices(true);
        try {
            //const response = await fetch(`${testingBase}${grabInvoices}?q=${encodeURIComponent(chosenQuery)}`, {
                const response = await fetch(`${prodBase}${grabInvoices}?q=${encodeURIComponent(chosenQuery)}`, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) throw new Error('Failed to fetch invoice files');

            const data = await response.json();
            const invoices = data.files || [];

            console.log('Refreshed invoice files from OneDrive:', invoices.length);
            return invoices;
        } catch (error) {
            console.error('Error fetching invoice files:', error);
            toast('Failed to load invoice files', { className: 'error-toast', autoClose: 3000 });
        } finally {
            setIsLoadingInvoices(false);
        }
    };

    function toggleSelected(id) {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }

    const allSelected = invoiceFiles.length > 0 && selectedIds.size === invoiceFiles.length;
    const someSelected = selectedIds.size > 0 && !allSelected;

    function toggleSelectAll() {
        setSelectedIds((prev) => {
            if (invoiceFiles.length === 0) return new Set();
            if (prev.size === invoiceFiles.length) return new Set(); // unselect all
            return new Set(invoiceFiles.map((f) => f.id)); // select all visible
        });
    }

    const selectedFiles = useMemo(() =>
        invoiceFiles.filter((f) => selectedIds.has(f.id)),
        [invoiceFiles, selectedIds]);

    /**
     * Fetches an archived invoice through the backend.
     *
     * The search endpoint used to hand the browser a tenant-wide Graph token
     * alongside a direct Graph download URL. It no longer does - it returns a
     * contentPath into /api/invoicecontent, which streams the file server-side.
     * webUrl is the last resort: it opens OneDrive in a tab and relies on the
     * operator's own session.
     */
    async function fetchInvoiceBlob(file) {
        const path = file.contentPath
            ? `${prodBase}${file.contentPath}`
            : file.downloadUrl || null;

        if (!path) throw new Error('No download source for this file');

        const response = await fetch(path);
        if (!response.ok) throw new Error(`Failed to download file: ${response.status}`);
        return response.blob();
    }

    function saveBlob(blob, name) {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    }

    async function handleDownloadSelected() {
        if (selectedFiles.length === 0) {
            throw new Error("No files selected");
            return;
        }

        setDownloading(true);

        let ok = 0, fail = 0;

        for (const file of selectedFiles) {
            try {
                if (file.contentPath || file.downloadUrl) {
                    saveBlob(await fetchInvoiceBlob(file), file.name);
                    ok++;
                } else {
                    const url = `${file.webUrl}${file.webUrl.includes('?') ? '&' : '?'}download=1`;
                    window.open(url, '_blank');
                    ok++;
                }

                // Small delay to reduce popup blocking / throttling
                await new Promise((r) => setTimeout(r, 120));

            } catch (e) {
                fail++;
            }
        }
        setDownloading(false);

        if (ok && !fail) {
            toast(`Downloaded ${ok} file${ok === 1 ? "" : "s"}.`, { autoClose: 2500, className: "success-toast" });
        } else if (ok && fail) {
            toast(`Downloaded ${ok}, ${fail} failed.`, { className: "error-toast", autoClose: 3500 });
        } else {
            toast("All downloads failed. Please try again.", { className: "info-toast", autoClose: 3500 });
        }
    }

    function handleFileClick(file, index) {
        setSelectedFileIndex(index);
        if (typeof onFileSelect === 'function') {
            onFileSelect(file, index);
        }
    }

    async function handleDownloadSingle(file, event) {
        event.preventDefault();
        event.stopPropagation();
        try {
            if (!file.contentPath && !file.downloadUrl) {
                if (!file.webUrl) throw new Error('No download source for this file');
                window.open(`${file.webUrl}${file.webUrl.includes('?') ? '&' : '?'}download=1`, '_blank', 'noopener');
                return;
            }

            saveBlob(await fetchInvoiceBlob(file), file.name);
            toast(`Downloading ${file.name}`, { autoClose: 3000, className: 'success-toast' });

        } catch (e) {
            toast(`Download failed: ${e?.message || String(e)}`, { autoClose: 3000, className: 'error-toast' });
        }
    }

    return (
        <div className="w-full rounded-2xl border p-4 mt-2 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">

                <div className="flex flex-col">
                    <Label htmlFor="voucher" className="font-medium mb-2">Voucher No.</Label>
                    <Input
                        id="voucher"
                        inputMode="text"
                        placeholder="22966 or V22966"
                        value={voucher}
                        onChange={(e) => {
                            let value = e.target.value.toUpperCase();
                            if (value === '' || value === 'V') {
                                setVoucher(value);
                            } else if (/^V?\d+$/.test(value)) {
                                setVoucher(value);
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                    />
                    {(!/^V?\d+$/.test(voucher) && voucher) && <span className="text-red-600 text-sm">Only numbers allowed</span>}
                </div>

                <div className="flex flex-col">
                    <Label htmlFor="vendor" className="font-medium mb-2">Vendor Code</Label>
                    <Input
                        id="vendor"
                        placeholder="SCB"
                        value={vendor}
                        onChange={(e) => setVendor(e.target.value.toUpperCase())}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                    />
                </div>

                <div className="flex flex-col">
                    <Label htmlFor="invoice" className="font-medium mb-2">Invoice No.</Label>
                    <Input
                        id="invoice"
                        inputMode="numeric"
                        placeholder="2023147"
                        value={invoice}
                        onChange={(e) => setInvoice(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                    />
                </div>

                <div className="flex flex-col">
                    <Label htmlFor="startDate" className="font-medium mb-2">Start Date</Label>
                    <Input
                        id="startDate"
                        placeholder="11.2020 or 2020"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                    />
                </div>

                <div className="flex flex-col">
                    <Label htmlFor="endDate" className="font-medium mb-2">End Date</Label>
                    <Input
                        id="endDate"
                        placeholder="11.2025 or 2025"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                    />
                </div>

                <div className="flex items-end m-[1.5rem]">
                    <div className="flex items-center gap-2">
                        <Checkbox id="fa" checked={faOnly} onCheckedChange={(v) => setFaOnly(!!v)} />
                        <Label htmlFor="fa" className="font-medium mb-1">Fixed Asset only</Label>
                    </div>
                </div>


            </div>
            <div className="flex items-center justify-end">
                <Button onClick={handleSearch} disabled={loading} className="mr-3" >
                    {loading ? <Loader2 className="h-4 w-4 animate-spin " /> : null}
                    Search files
                </Button>
            </div>
            <div className="m-auto justify-end flex items-center">
                <span className="text-sm text-blue-400">
                    {invoiceFiles.length ? `${invoiceFiles.length} match${invoiceFiles.length === 1 ? "" : "es"}` : ""}
                </span>
                {error ? <span className="text-red-600 text-sm">{error}</span> : null}
            </div>
            {invoiceFiles.length > 0 && (
                <div className="rounded-xl border-[3px] border-gray-300">
                    {/* Scrollable list */}
                    <ul className="divide-y max-h-80 overflow-y-auto" aria-label="Invoice Files">
                        {invoiceFiles.map((f, index) => (
                            <li key={f.id}
                                className={`p-3 flex items-center justify-between min-h-16 cursor-pointer transition-colors ${selectedFileIndex === index
                                    ? 'bg-primary/10 border-l-4 border-primary'
                                    : 'hover:bg-muted/50'
                                    }`}
                                onClick={() => handleFileClick(f, index)}>
                                {/* LEFT: checkbox + name */}
                                <div className="flex items-start gap-3 min-w-0">
                                    <Checkbox
                                        checked={selectedIds.has(f.id)}
                                        onCheckedChange={(e) => { toggleSelected(f.id) }}
                                        onClick={(e) => e.stopPropagation()}
                                        aria-label={`Select ${f.name}`}
                                        className="mt-1"
                                    />
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <div className="font-medium truncate">{f.name}</div>
                                            {selectedFileIndex === index && (
                                                <Badge variant="default" className="bg-blue-600 flex-shrink-0">
                                                    Active
                                                </Badge>
                                            )}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            {new Date(f.lastModified).toLocaleString()} • {(f.size / 1024).toFixed(1)} KB
                                        </div>
                                    </div>
                                </div>

                                {/* RIGHT: actions */}
                                <div className="flex gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                                    <button
                                        onClick={(e) => handleDownloadSingle(f, e)}
                                        className="underline text-sm cursor-pointer hover:text-primary"
                                    >
                                        Download
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Footer with Select All + counters + bulk action */}
            <div className="flex items-center gap-3 p-3">
                {/* Select all (left of the Download button) */}
                <div className="flex items-center gap-2">
                    <Checkbox
                        checked={allSelected}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Select all files"
                    // shadcn doesn't expose 'indeterminate' directly—optional:
                    // className={someSelected ? "data-[state=indeterminate]:opacity-100" : ""}
                    />
                    <span className="text-sm">Select all</span>
                </div>

                {/* Files selected counter (muted, bottom-left per your spec) */}
                <div className="text-sm text-muted-foreground ml-4">
                    Files Selected: {selectedIds.size}
                </div>

                <div className="ml-auto">
                    <Button
                        variant="default"
                        onClick={handleDownloadSelected}
                        disabled={!selectedIds.size || downloading}
                    >
                        {downloading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Download Selected
                    </Button>
                </div>
            </div>
        </div>
    );
}
